import { History, ClipboardList, Timer } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useMyUpcomingEvents,
  useMyEventHistory,
} from '@/api/extensions/useDashboardData';
import { getRelativeTime } from '@/utils/formatters';
import { useMemo } from 'react';

export function UserStatsCards() {
  const { data: upcoming, isLoading: upcomingLoading } = useMyUpcomingEvents();
  // Pull only the count from event history — the default 'summary' view skips
  // the event-standings work that resolving placements and prize money costs,
  // so this card stays cheap while still giving us the authoritative `total`
  // of entries the user was actually scored in (the same filter
  // EventHistorySection uses). The previous "Events Competed" label was driven
  // by registration status alone, which counted future confirmed registrations
  // as already-competed.
  const {
    data: history,
    isLoading: historyLoading,
    isError,
    refetch,
  } = useMyEventHistory();

  const isLoading = historyLoading || upcomingLoading;

  // Find soonest upcoming event
  const nextEvent = useMemo(() => {
    if (!upcoming || upcoming.length === 0) return null;
    // Events are already sorted by rodeo start_date ascending from API
    const first = upcoming[0] as any;
    const rodeo = first.rodeo || {};
    const event = first.event || {};
    return {
      name: event.event_name || rodeo.name || 'Event',
      date: rodeo.start_date,
    };
  }, [upcoming]);

  if (isError) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">
        Failed to load stats.{' '}
        <button onClick={() => refetch()} className="underline font-medium">
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-xl" />
        ))}
      </div>
    );
  }

  const pastEventCount = history?.total ?? 0;
  const upcomingCount = upcoming?.length ?? 0;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
      <StatCard
        icon={ClipboardList}
        label="Entered"
        value={String(upcomingCount)}
        description={upcomingCount === 1 ? '1 active entry' : `${upcomingCount} active entries`}
      />
      <StatCard
        icon={History}
        label="Event History"
        value={String(pastEventCount)}
        description={pastEventCount === 1 ? '1 past event' : 'Past events'}
      />
      <StatCard
        icon={Timer}
        label="Next Event"
        value={nextEvent?.date ? getRelativeTime(nextEvent.date) || 'Soon' : '--'}
        description={nextEvent?.name || 'No events scheduled'}
        smallValue
      />
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  description,
  smallValue,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  description: string;
  smallValue?: boolean;
}) {
  // Layout mirrors the StatCard on RodeoDetailPage: icon + label sit in the
  // top-left, value (and supporting description) are centered. Keeps the
  // dashboard visually consistent with the rodeo detail header.
  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2">
      <div className="flex items-center gap-1.5">
        <Icon className="h-4 w-4 text-primary" />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p
        className={`font-bold tracking-tight text-center ${smallValue ? 'text-base' : 'text-2xl'}`}
      >
        {value}
      </p>
      <p className="text-xs text-muted-foreground text-center truncate">
        {description}
      </p>
    </div>
  );
}
